import { cn } from "@postcheesy/core-utils";
import React, { HTMLAttributes, Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container } from "./container";
import { Button } from "@postcheesy/ui-lib/atoms";
import { CardTitle } from "@postcheesy/ui-lib/molecules";
import { User, Store } from "lucide-react";
import { Cheese3 } from "../../asset/cheeseIcons/cheese3";
import { useUser } from "../../resources/contexts/UserContext";
import { LogoWithLogout } from "../ui/LogoWithLogout";

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({
  children,
  className,
  ...props
}) => {
  const links = {
    "/dashboard": {
      label: "Mes fromages",
      icon: <Cheese3 className="h-5 w-5" color="#FAFAFA" />,
    },
    "/locations": {
      label: "Boutiques",
      icon: <Store className="text-primary-foreground h-5 w-5" />,
    },
    "/profile": {
      label: "Mon profil",
      icon: <User className="text-primary-foreground h-5 w-5" />,
    },
  };

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuToggle = () => setIsMenuOpen(!isMenuOpen);

  const location = useLocation();
  const { isActive } = useUser();

  return (
    <header
      {...props}
      id="header"
      className={cn(
        className,
        "sticky top-0 z-40 w-full bg-primary dark:border-muted py-4",
      )}
    >
      <Container>
        <Suspense fallback={<p>Loading...</p>}>
          <div className="flex h-16 items-center space-x-4 justify-between">
            <LogoWithLogout />
            <nav className="hidden sm:flex">
              {isActive && Object.entries(links).map(([href, { label, icon }]) => (
                <Button
                  variant="link"
                  className="nav-link flex items-center space-x-2"
                  disabled={location.pathname === href}
                  key={href}
                >
                  <span className="sr-only">{label}</span>
                  {icon}
                  <Link to={href} className="text-background">
                    {label}
                  </Link>
                </Button>
              ))}
            </nav>
            {isActive && (
              <Button
                variant="link"
                className="sm:hidden"
                onClick={menuToggle}
              >
                {isMenuOpen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#ffffff"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#ffffff"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </Button>
            )}
            {isMenuOpen && isActive && (
              <div className="absolute right-0 top-16 z-10 w-full bg-primary dark:bg-[#161616]">
                <nav className="flex flex-col space-y-4 p-4">
                  {Object.entries(links).map(([href, { label, icon }]) => (
                    <Button
                      variant="link"
                      className="nav-link flex items-center space-x-2"
                      disabled={location.pathname === href}
                      key={href}
                    >
                      <span className="sr-only">{label}</span>
                      {icon}
                      <Link
                        to={href}
                        onClick={menuToggle}
                        className="text-background"
                      >
                        {label}
                      </Link>
                    </Button>
                  ))}
                </nav>
              </div>
            )}
          </div>
        </Suspense>
      </Container>
    </header>
  );
};

export const LogoYan: React.FC<HTMLAttributes<SVGAElement>> = ({
  ...props
}) => {
  return (
    <div>
      <svg
        className="min-w-fitmin-w h-[32px] min-h-max sm:h-[40px]"
        // width="136"
        height="40"
        viewBox="0 0 136 49"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M39.855 20.3593C39.5535 21.3411 39.7621 21.55 37.9531 22.6652C35.9637 23.8888 28.9701 23.9874 28.9701 23.9874C26.5653 23.9874 27.0951 20.8105 27.5489 19.1171C28.0037 17.4222 29.1174 16.5353 30.7156 15.7732C35.309 13.5869 41.4973 12.7834 41.8967 12.7346L39.855 20.3593ZM41.9837 6.6803H30.6279H29.8664L29.1245 9.45435H30.6471C30.6471 9.45435 30.9182 8.43604 30.9801 8.20288C31.0828 7.55383 30.8965 6.99719 41.2951 7.27612C43.6162 7.08911 42.7996 9.3689 42.6993 9.73853L41.9983 12.3536C41.405 12.4423 33.27 13.6725 30.701 14.886C28.0321 16.1442 26.5951 17.0054 26.0277 19.1171C25.462 21.2292 25.4452 24.3782 28.5707 24.3782H37.5735C39.0508 24.3782 40.6327 23.1313 41.0291 21.6532L44.2206 9.73853C44.5474 8.52148 43.8228 6.6803 41.9837 6.6803ZM101.653 22.4646H92.6594C91.1066 22.4646 90.5769 21.5325 90.6328 19.6956C90.6858 17.861 91.8943 11.8246 92.3593 10.4332C92.8234 9.03894 94.201 6.91858 96.0724 6.81165C104.243 6.34851 104.96 6.83301 105.821 7.36829C106.609 7.85925 107.056 8.51501 107.056 8.51501L107.624 6.29028H96.5413C94.4595 6.29028 92.0701 8.64795 91.6719 10.1293L89.1096 19.6956C88.4643 22.1017 89.8451 23.9874 92.2521 23.9874H102.88L103.49 21.7961C103.49 21.7961 102.398 22.4646 101.653 22.4646ZM87.1639 6.29028C87.1639 6.29028 88.1871 7.00867 87.9938 7.92542C85.9528 17.6735 84.1288 21.5577 83.7247 22.5023C83.3155 23.4539 82.4383 23.9874 82.4383 23.9874H84.5162L84.8148 22.7341L89.2463 6.29028H87.1639ZM133.793 10.2148C133.793 10.2148 134.23 11.2889 134.044 12.1039C132.28 19.9025 128.483 22.4095 117.241 22.4095C115.012 22.4095 113.228 21.7961 113.228 21.7961L112.395 24.4572C112.395 24.4572 113.055 23.9874 114.134 23.9874H117.635C127.934 23.9874 129.107 28.2068 127.403 34.9708H126.533L126.26 35.9858H128.155L128.291 35.4791C130.134 28.6029 128.854 24.8182 123.873 23.391C130.741 21.8749 133.357 17.5222 135.112 10.9756L135.315 10.2148H133.793ZM115.449 0.749268C115.449 0.749268 115.852 1.33411 115.642 2.11548C115.454 2.81726 105.701 39.2148 103.654 46.8524C103.414 47.7477 102.938 48.2468 102.938 48.2468H104.802L117.53 0.749268H115.449ZM10.6127 48.2468H12.7732C12.7732 48.2468 12.2778 47.71 12.5367 46.749C14.626 38.9479 24.1689 3.31482 24.4038 2.45752C24.6709 1.49487 25.36 0.749268 25.36 0.749268H23.339L10.6127 48.2468ZM13.9593 23.4913C15.5449 23.4913 16.0303 23.9874 16.0303 23.9874L16.63 22.2839L10.2029 22.821C0.292044 22.821 0.801199 14.4688 4.25847 1.56531L4.46172 0.804932H3.70012H2.06328L1.65435 2.32739H2.53631C-0.25654 12.9497 -1.45069 23.4913 10.6459 23.4913H13.9593ZM58.9209 6.29028H49.4087H48.1789C48.1789 6.29028 48.4535 7.01221 48.2453 7.77917C48.1473 8.15186 47.113 12.0133 46.0797 15.8717C44.9912 19.9303 43.9054 23.9874 43.9054 23.9874H45.8865C45.8865 23.9874 45.5725 23.442 45.8122 22.5502C46.8173 18.7992 49.5906 8.53308 49.7609 7.81189C49.9301 7.08911 49.9301 6.64905 58.9356 6.99719C61.1945 7.08423 62.9619 9.67456 62.4621 11.6389C59.8632 21.8516 58.0654 23.9874 58.0654 23.9874H59.5876L62.881 11.6982C63.4856 9.43616 61.605 6.29028 58.9209 6.29028ZM77.9319 6.29028H68.4179H67.1895C67.1895 6.29028 67.4611 7.01221 67.2565 7.77917C67.1561 8.15186 66.1238 12.0133 65.0903 15.8717C64.0011 19.9303 62.9136 23.9874 62.9136 23.9874H64.8982C64.8982 23.9874 64.5807 23.442 64.8224 22.5502C65.8262 18.7992 68.6017 8.53308 68.769 7.81189C68.9386 7.08911 68.9386 6.64905 77.9435 6.99719C80.2043 7.08423 81.9734 9.67456 81.4727 11.6389C78.8723 21.8516 77.0757 23.9874 77.0757 23.9874H78.5975L81.8915 11.6982C82.4959 9.43616 80.6135 6.29028 77.9319 6.29028Z"
          fill="#5A504D"
        />
        <path
          d="M19.6162 38.0012L22.139 38.4656H99.8484L97.3261 38.0012H19.6162Z"
          fill="#5A504D"
        />
        <path
          d="M25.5292 34.9363L26.2507 31.1074H25.666L25.774 30.5134H26.3696L26.4059 30.2764C26.5162 29.6628 26.7344 29.0972 27.1273 28.7405C27.4111 28.4861 27.7941 28.3481 28.1781 28.3481C28.4904 28.3481 28.6809 28.4041 28.791 28.468L28.627 29.071C28.5066 29.0149 28.3528 28.9883 28.1781 28.9883C27.5758 28.9883 27.2832 29.5732 27.1544 30.3025L27.1185 30.5134H28.1058L27.9862 31.1074H27.0087L26.2877 34.9363H25.5292Z"
          fill="#5A504D"
        />
        <path
          d="M31.6591 34.9359L32.1967 32.0861C32.3077 31.5114 32.3718 30.924 32.4161 30.5131H33.083C33.0566 30.8229 33.0192 31.1251 32.965 31.4735H32.9927C33.2848 30.869 33.7499 30.4127 34.3446 30.4127C34.4169 30.4127 34.4984 30.422 34.5629 30.4303L34.4169 31.1796C34.3619 31.1713 34.2899 31.1615 34.2065 31.1615C33.5596 31.1615 32.9755 31.9584 32.7826 32.9813L32.4161 34.9359H31.6591Z"
          fill="#5A504D"
        />
        <path
          d="M40.0437 34.4332C40.9388 34.4332 41.6149 33.2836 41.6149 32.2506C41.6149 31.7396 41.3767 31.0153 40.5828 31.0153C39.6333 31.0153 39.0026 32.1781 39.0026 33.1915C39.0026 33.913 39.4048 34.4332 40.0358 34.4332H40.0437ZM39.953 35.0363C38.8483 35.0363 38.2273 34.215 38.2273 33.2194C38.2273 31.7579 39.2408 30.4127 40.6738 30.4127C41.8721 30.4127 42.4105 31.3173 42.4105 32.2506C42.4105 33.6202 41.4417 35.0363 39.9618 35.0363H39.953Z"
          fill="#5A504D"
        />
        <path
          d="M46.5941 34.9359L47.1869 31.8038C47.2781 31.3075 47.3431 30.8788 47.3889 30.5131H48.0544L47.9369 31.3439H47.9628C48.3741 30.7225 48.9307 30.4127 49.516 30.4127C50.1919 30.4127 50.5121 30.8427 50.5492 31.3998C50.9415 30.7609 51.5083 30.422 52.1654 30.4127C52.6776 30.4127 53.2428 30.7411 53.2428 31.5753C53.2428 31.7758 53.2162 32.0763 53.1704 32.3243L52.6776 34.9359H51.937L52.412 32.3868C52.4495 32.2223 52.4751 31.9943 52.4751 31.8119C52.4751 31.3439 52.3029 31.0433 51.8088 31.0433C51.2622 31.0433 50.6031 31.6837 50.4208 32.6708L50.0007 34.9359H49.2689L49.7621 32.3424C49.8007 32.1405 49.818 31.9752 49.818 31.8038C49.818 31.4293 49.7172 31.0433 49.1595 31.0433C48.6028 31.0433 47.9181 31.7758 47.7435 32.7169L47.3326 34.9359H46.5941Z"
          fill="#5A504D"
        />
        <path
          d="M61.0533 31.0792C60.9441 31.0433 60.7887 31.0153 60.5326 31.0153C59.3911 31.0153 58.4489 32.2045 58.4489 33.4381C58.4489 33.9305 58.6248 34.4154 59.1989 34.4154C59.8395 34.4154 60.5883 33.5941 60.7986 32.4147L61.0533 31.0792ZM60.5702 34.9359C60.5702 34.6713 60.6149 34.2511 60.6702 33.7943H60.6522C60.1681 34.7177 59.5542 35.0363 58.9342 35.0363C58.1493 35.0363 57.6636 34.4154 57.6636 33.5743C57.6636 32.0321 58.7979 30.4127 60.7164 30.4127C61.1349 30.4127 61.6027 30.4867 61.9038 30.5768L61.4554 32.8895C61.3086 33.6581 61.2552 34.4427 61.2729 34.9359H60.5702Z"
          fill="#5A504D"
        />
        <path
          d="M69.4928 31.172C69.3473 31.098 69.0726 31.0155 68.7628 31.0155C67.6573 31.0155 66.8898 32.1786 66.8898 33.274C66.8802 33.767 67.0643 34.3336 67.7396 34.3336C68.3883 34.3336 69.0465 33.5123 69.2106 32.6434L69.4928 31.172ZM66.0956 35.9415C66.3597 36.1154 66.8085 36.234 67.2638 36.234C68.0148 36.234 68.5985 35.8595 68.8553 34.662L69.0011 33.995H68.983C68.5885 34.6263 68.0507 34.9364 67.4464 34.9364C66.5516 34.9364 66.0956 34.1871 66.0956 33.4025C66.0956 31.8947 67.1925 30.4127 68.9008 30.4127C69.4115 30.4127 69.9781 30.5592 70.3246 30.7235L69.64 34.3798C69.4657 35.3207 69.2106 35.9598 68.754 36.3622C68.306 36.7369 67.7133 36.8448 67.2106 36.8448C66.6718 36.8448 66.1595 36.7184 65.8854 36.5345L66.0956 35.9415Z"
          fill="#5A504D"
        />
        <path
          d="M77.514 31.5658C77.514 31.2074 77.2399 31.0074 76.8373 31.0074C76.1163 31.0074 75.5768 31.7115 75.3942 32.4066C76.5819 32.424 77.514 32.2604 77.514 31.5753V31.5658ZM77.6786 34.6893C77.3492 34.8719 76.8104 35.0363 76.2164 35.0363C75.0472 35.0363 74.5363 34.233 74.5363 33.2557C74.5363 31.922 75.5206 30.4127 76.9476 30.4127C77.788 30.4127 78.245 30.9149 78.245 31.5555C78.245 32.7172 76.9366 33.0175 75.3031 32.9913C75.2684 33.182 75.2936 33.5941 75.3942 33.8307C75.5692 34.233 75.9232 34.4252 76.3807 34.4252C76.9012 34.4252 77.2941 34.2689 77.5668 34.1232L77.6786 34.6893Z"
          fill="#5A504D"
        />
        <path
          d="M82.2821 34.9359L82.8207 32.0861C82.931 31.5114 82.995 30.924 83.0389 30.5131H83.7057C83.6796 30.8229 83.6429 31.1251 83.5883 31.4735H83.6161C83.9083 30.869 84.3729 30.4127 84.9669 30.4127C85.0392 30.4127 85.121 30.422 85.1864 30.4303L85.0392 31.1796C84.9855 31.1713 84.9135 31.1615 84.8295 31.1615C84.1825 31.1615 83.5978 31.9584 83.4054 32.9813L83.0389 34.9359H82.2821Z"
          fill="#5A504D"
        />
        <path
          d="M90.2467 29.6997C89.9823 29.6997 89.799 29.4912 89.799 29.207C89.8085 28.9062 90.0363 28.6682 90.3199 28.6682C90.6017 28.6682 90.7953 28.8784 90.7953 29.1611C90.7841 29.4812 90.5573 29.6997 90.2565 29.6997H90.2467ZM88.8309 34.9363L89.6713 30.5129H90.4305L89.5895 34.9363H88.8309Z"
          fill="#5A504D"
        />
        <path
          d="M97.6007 31.5658C97.6007 31.2074 97.3263 31.0074 96.9249 31.0074C96.2018 31.0074 95.6637 31.7115 95.4811 32.4066C96.6686 32.424 97.6007 32.2604 97.6007 31.5753V31.5658ZM97.7633 34.6893C97.4362 34.8719 96.8971 35.0363 96.3033 35.0363C95.1359 35.0363 94.6217 34.233 94.6217 33.2557C94.6217 31.922 95.6095 30.4127 97.0323 30.4127C97.8741 30.4127 98.3307 30.9149 98.3307 31.5555C98.3307 32.7172 97.0255 33.0175 95.3893 32.9913C95.3531 33.182 95.3815 33.5941 95.4811 33.8307C95.6554 34.233 96.0128 34.4252 96.4684 34.4252C96.9894 34.4252 97.3815 34.2689 97.6554 34.1232L97.7633 34.6893Z"
          fill="#5A504D"
        />
      </svg>
    </div>
  );
};
